import {Directive, inject, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {ColDef, GridApi, GridReadyEvent} from "ag-grid-community";
import {IPlayer} from "../../interfaces/player/IPlayer";
import {PlayersService} from "../../services/players.service";
import {PhoneFormatPipe} from "../../pipes/phone-format.pipe";
import {Router} from "@angular/router";
import {ErrorHandlingService} from "../../services/error-handling.service";
import {IPlayerSearchParams} from "../../interfaces/player/IPlayerSearchParams";
import {UIStateEnum} from "../../enum/UIStateEnum";
import {ITableExportRequest, TableExportService} from "../../services/table-export.service";
import {ActiveUserService} from "../../services/active-user.service";

@Directive()
export abstract class BasePlayerSearchComponent implements OnInit {
  public uiState: UIStateEnum = UIStateEnum.ShowLoading;
  public uiStateEnumForTemplate = UIStateEnum;
  // a partial phone number matches too many players, so only a full 10-digit number is searchable
  private fullPhoneNumberRegex = /^\d{3}-\d{3}-\d{4}$/;
  public phoneNumberControl: FormControl<string | null> = new FormControl<string | null>('', Validators.pattern(this.fullPhoneNumberRegex));
  private guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  public playerIdControl: FormControl<string | null> = new FormControl('', Validators.pattern(this.guidRegex));

  public playerSearchForm = new FormGroup({
    firstName: new FormControl(''),
    lastName: new FormControl(''),
    email: new FormControl(''),
    phoneNumber: this.phoneNumberControl,
    playerId: this.playerIdControl
  });

  public players: IPlayer[] = [];

  /**
   * How many players matched before the server's cap. The search returns at most
   * a fixed number of rows (PlayerSqlRepository), so without this the screen would show a trimmed list
   * with nothing to say it was trimmed — and the export would carry that same silence out of the app.
   */
  public totalMatches = 0;

  /** True when the cap trimmed the answer, so the screen has to say what is missing. */
  public get resultsAreTruncated(): boolean {
    return this.totalMatches > this.players.length;
  }

  public columnDefs: ColDef[] = [
    {headerName: 'Id', field: 'Id', resizable: true, sortable: true},
    {headerName: 'First Name', field: 'FirstName', resizable: true, sortable: true},
    {headerName: 'Last Name', field: 'LastName', resizable: true, sortable: true},
    {headerName: 'Email', field: 'Email', resizable: true, sortable: true},
    {
      headerName: 'Phone',
      field: 'Phone',
      valueFormatter: params => {
        if (params.value && params.value.includes('***')) {
          return params.value;
        }
        return this.phoneFormatPipe.transform(params.value);
      },
      resizable: true,
      sortable: true
    },
  ];

  private tableExportService: TableExportService = inject(TableExportService);
  private activeUserService: ActiveUserService = inject(ActiveUserService);

  /**
   * The search results as shown, for CSV and PDF (#1411: "anytime there is a table of information, have an export
   * by pdf and csv option"). Defined once here because both player searches — game-scoped and global — render the
   * same columns, and two copies of an export are two chances for one of them to drift from its screen.
   *
   * The values exported are the ones on screen, which for these searches means <b>masked</b>: both callers search
   * with maskData, so the file carries partial emails and phone numbers exactly as displayed. That is deliberate —
   * an export must not reveal more than the screen it came from. It was also not true until this MR: the
   * game-scoped API route dropped the caller's maskData and returned full contact details, which nobody could see
   * on a grid that prints whatever it is handed. See PlayerSearchMaskingTests.
   */
  private buildExportRequest(): ITableExportRequest<IPlayer> {
    return {
      title: 'Player search results',
      subtitle: `${this.exportScopeLabel()} — ${this.exportCountLabel()}`,
      rows: this.players,
      columns: [
        {header: 'Id', value: playerP => playerP.Id},
        {header: 'First name', value: playerP => playerP.FirstName ?? ''},
        {header: 'Last name', value: playerP => playerP.LastName ?? ''},
        {header: 'Email', value: playerP => playerP.Email ?? ''},
        {
          header: 'Phone',
          value: playerP => playerP.Phone && playerP.Phone.includes('***')
            ? playerP.Phone
            : this.phoneFormatPipe.transform(playerP.Phone) ?? ''
        }
      ],
      fileName: this.tableExportService.fileNameFor([...this.exportFileNameScope(), 'player-search'])
    };
  }

  /**
   * The count as the file should state it. When the cap trimmed the answer the honest sentence names both
   * numbers — a file of 500 rows that says "500 players matched" is wrong, and whoever opens it in a
   * spreadsheet has no way to tell from the rows.
   */
  private exportCountLabel(): string {
    return this.resultsAreTruncated
      ? `first ${this.players.length} of ${this.totalMatches} player(s) matching the search — narrow the search to export the rest`
      : `${this.players.length} player(s) matching the search`;
  }

  // A bulk file of player contact details is for Causable staff only; hosts look players up one at a time.
  private get mayExportPlayers(): boolean {
    return this.activeUserService.activeUser().CausableAdmin;
  }

  onExportCsv(): void {
    if (this.mayExportPlayers) {
      this.tableExportService.exportCsv(this.buildExportRequest());
    }
  }

  onExportPdf(): void {
    if (this.mayExportPlayers) {
      this.tableExportService.exportPdf(this.buildExportRequest());
    }
  }

  private gridApi!: GridApi<IPlayer>;

  protected constructor(
    protected playerService: PlayersService,
    protected phoneFormatPipe: PhoneFormatPipe,
    protected errorHandlingService: ErrorHandlingService,
    protected router: Router
  ) {
    this.phoneNumberControl.valueChanges.subscribe((value: string | null) => {
      if (value) {
        const formattedValue = this.phoneFormatPipe.transform(value);
        this.phoneNumberControl.setValue(formattedValue, {emitEvent: false});
      }
    });
  }

  ngOnInit(): void {
    this.retrieveUsersWithSearchParams(this.getSearchParams());
  }

  private retrieveUsersWithSearchParams(paramsP?: IPlayerSearchParams) {
    this.uiState = UIStateEnum.ShowLoading;

    this.playerService.getPlayersBySearchParams(paramsP, this.isGlobalSearch()).subscribe({
      next: res => {
        this.players = res.Players;
        this.totalMatches = res.TotalMatches;
        this.uiState = UIStateEnum.ShowData;
      },
      error: error => {
        this.uiState = UIStateEnum.ShowData;
        this.errorHandlingService.displayPageLevelErrorMessage(error);
      }
    });
  }

  onResetClick() {
    this.playerSearchForm.reset();
    this.retrieveUsersWithSearchParams(this.getSearchParams());
  }

  onFormSubmit() {
    if (!this.playerSearchForm.valid) {
      this.playerSearchForm.markAllAsTouched();
      return;
    }

    this.retrieveUsersWithSearchParams(this.getSearchParams());
  }

  onGridReady(params: GridReadyEvent<IPlayer>) {
    this.gridApi = params.api;
    this.gridApi.sizeColumnsToFit({defaultMinWidth: 100});
  }

  async onRowSelected(chosenPlayerP: any) {
    let player: IPlayer = chosenPlayerP.data;
    await this.router.navigateByUrl(this.getPlayerManagementUrl(player.Id));
  }

  protected abstract getSearchParams(): IPlayerSearchParams | undefined;

  protected abstract isGlobalSearch(): boolean;

  protected abstract getPlayerManagementUrl(playerId: string): string;

  /**
   * Which of the two searches this is, for the export. Both render the same table, so a file that says only
   * "Player search results" and lands as player-search-2026-08-20.csv is indistinguishable from the other one
   * in a downloads folder — and the game-scoped file never says which game it came from.
   */
  protected abstract exportScopeLabel(): string;

  /** Leading part of the exported file name, keeping the two searches' files apart on disk. */
  protected abstract exportFileNameScope(): (string | null | undefined)[];

  /** Prefix for the export buttons' test ids — the one thing the two search templates do not share. */
  protected abstract exportTestIdPrefix(): string;
}
